import React, { useCallback, useEffect, useReducer } from 'react';
import { HyperLink } from '@src/types/contentful';
import { Maybe } from '@src/graphql/gql-types';
import LinkWrapper from '@src/components/LinkWrapper';
import { PATHS, SELECT_EVENT_TAB_QUERY_PARAM } from '@src/constants/site';
import styles from './MobileNestedMenu.module.scss';
import classNames from 'classnames';
import { COLOR_LIME, COLOR_WHITE } from '@src/types/colors';
import { ButtonLinkStandard } from '@src/components/ButtonLink';
import BoxIcon from '@src/elements/BoxIcon';
import { ButtonStandard } from '@src/components/Button';
import router from 'next/router';
import { handleEnquireButtonScrollToClick } from '@src/utils/scrollToEnquireForm';
import { useViewport } from '@src/hooks/useViewport';

interface MobileNestedMenu {
  openState: {
    open: boolean;
    resetMenu: boolean;
  };
  menuLinks: HyperLink[];
  communityNav?: HyperLink[];
  handleCloseMobileMenu: (event: React.MouseEvent) => void;
  enquireButtonScrollTo: string;
  headOfficePhoneNumber?: Maybe<string>;
}

interface Menu {
  menuLinks: Maybe<HyperLink[]>;
  title: Maybe<string>;
  index: number;
  state: NestedMenuState;
  dispatch: (action: MenuAction) => void;
  handleCloseMobileMenu: (event: React.MouseEvent) => void;
  width?: number;
}

interface EnquireMenu {
  state: NestedMenuState;
  dispatch: (action: MenuAction) => void;
  width: number;
  handleCloseMobileMenu: (event: React.MouseEvent) => void;
  enquireButtonScrollTo: string;
}

type MenuAction = {
  type: 'forward' | 'back' | 'reset';
  payload?: {
    title: Maybe<string>;
  };
};

type MenuState = {
  title: Maybe<string>;
};

type NestedMenuState = {
  active: MenuState;
  previous: MenuState[];
};

const Menu = ({
  state,
  dispatch,
  menuLinks,
  title,
  index,
  handleCloseMobileMenu
}: Menu) => {
  if (!menuLinks) {
    return null;
  }

  const isNestedSunshineCoastMenu =
    state.previous?.[state.previous.length - 1]?.title === 'Sunshine Coast';

  return (
    <div
      data-testid={`mobileMenuAccordion-${index}`}
      className={classNames(styles.mobileMenuUl, {
        [styles.mobileMenuUlActive]: title === state.active.title,
        [styles.mobileMenuUlPrevious]:
          state.previous?.length > 0 &&
          !isNestedSunshineCoastMenu &&
          title === state.previous?.[state.previous.length - 1]?.title,
        [styles.mobileMenuUlNested]: !!title
      })}
    >
      <ul>
        {menuLinks.map(
          ({ subNav, urlPath, title, openNewWindow, sys }, index) => {
            // adds query param on to end of path for News & Events page so that the
            // page renders with the news tab open.
            const href = urlPath?.includes(PATHS.COMMUNITY_NEWS_AND_EVENTS)
              ? urlPath + SELECT_EVENT_TAB_QUERY_PARAM
              : urlPath;

            return (
              <li
                key={`${sys?.id}-${index}`}
                className={classNames(styles.mobileMenuListItem, {
                  [styles.mobileMenuListItemFirstButton]: index === 0,
                  [styles.mobileMenuListItemHasSub]: !!subNav
                })}
                {...(subNav && {
                  onClick: () => {
                    return dispatch({
                      type: 'forward',
                      payload: {
                        title: title
                      }
                    });
                  }
                })}
              >
                <LinkWrapper
                  href={href as string}
                  openNewWindow={!!openNewWindow}
                  onClick={handleCloseMobileMenu}
                >
                  {title}
                </LinkWrapper>

                {subNav && <BoxIcon name="right-arrow-alt" />}
              </li>
            );
          }
        )}
      </ul>
    </div>
  );
};

const EnquireMenu = ({
  width,
  state,
  dispatch,
  handleCloseMobileMenu,
  enquireButtonScrollTo
}: EnquireMenu) => (
  <div
    data-testid={`mobileMenuEnquireAccordion`}
    className={classNames(styles.mobileMenuUl, {
      [styles.mobileMenuUlActive]: state.active.title === 'Enquire',
      [styles.mobileMenuUlPrevious]: false,
      [styles.mobileMenuUlNested]: false
    })}
  >
    <ul>
      <li
        className={classNames(styles.mobileMenuListItem, {
          [styles.mobileMenuListItemFirstButton]: true,
          [styles.mobileMenuListItemHasSub]: false
        })}
      >
        <button
          onClick={(event) => {
            handleEnquireButtonScrollToClick(
              width,
              router,
              event,
              handleCloseMobileMenu,
              enquireButtonScrollTo
            );
            dispatch({ type: 'reset' });
          }}
        >
          General enquiry
        </button>
      </li>

      <li
        className={classNames(styles.mobileMenuListItem, {
          [styles.mobileMenuListItemHasSub]: false
        })}
      >
        <button
          onClick={(event) => {
            handleEnquireButtonScrollToClick(
              width,
              router,
              event,
              handleCloseMobileMenu,
              enquireButtonScrollTo,
              true
            );
            dispatch({ type: 'reset' });
          }}
        >
          Book a tour
        </button>
      </li>

      <li
        className={classNames(styles.mobileMenuListItem, {
          [styles.mobileMenuListItemHasSub]: false
        })}
      >
        <LinkWrapper
          href="/careers-at-levande/"
          onClick={(event) => {
            handleCloseMobileMenu(event);
            dispatch({ type: 'reset' });
          }}
        >
          Careers
        </LinkWrapper>
      </li>
    </ul>
  </div>
);

const MobileNestedMenu: React.FC<MobileNestedMenu> = ({
  openState,
  menuLinks,
  communityNav,
  handleCloseMobileMenu,
  enquireButtonScrollTo,
  headOfficePhoneNumber
}) => {
  const { width } = useViewport();

  const insertCommunityNav = menuLinks.map((item) => {
    if (item.showCommunityMegaMenu) {
      return {
        ...item,
        subNav: communityNav
      };
    }
    return item;
  });
  const findYourCommunityMenu = insertCommunityNav.filter(
    (item) => item.title === 'Find your community'
  )?.[0]?.subNav;
  const initialActive = { title: undefined };
  const initialState = {
    active: initialActive,
    previous: []
  };

  // Handles resetting of menus state when the mobile menu is closed
  useEffect(() => {
    if (openState.resetMenu) {
      dispatch({ type: 'reset' });
    }
  }, [openState.resetMenu]);

  const reducer = useCallback(
    (state: NestedMenuState, action: MenuAction): NestedMenuState => {
      switch (action.type) {
        case 'forward': {
          const previous = [...state.previous, state.active];
          return {
            active: action.payload || { title: undefined },
            previous
          };
        }
        case 'back': {
          const active = [...state.previous][state.previous.length - 1];
          const previous = state.previous.slice(0, -1);
          return {
            active,
            previous
          };
        }
        case 'reset':
        default:
          return initialState;
      }
    },
    [initialState]
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const getFindYourCommunityMenuByLevel = (
    level: 3 | 4 | 5,
    state: NestedMenuState,
    findYourCommunityMenu: HyperLink[]
  ) => {
    const activeTitle = state.active.title;
    const previousTitle = state.previous?.[state.previous?.length - 1]?.title;
    const secondPreviousTitle =
      state.previous?.[state.previous?.length - 2]?.title;

    if (level === 3) {
      const thirdMenu = findYourCommunityMenu?.find(
        (item) => item.title === activeTitle
      )?.subNav;
      return thirdMenu;
    }

    if (level === 4) {
      const thirdMenu = findYourCommunityMenu?.find(
        (item) => item.title === previousTitle
      )?.subNav;
      const fourthMenu = thirdMenu?.find((item) => item.title === activeTitle)
        ?.subNav;
      return fourthMenu;
    }

    if (level === 5) {
      const thirdMenu = findYourCommunityMenu?.find(
        (item) => item.title === secondPreviousTitle
      )?.subNav;
      const fourthMenu = thirdMenu?.find((item) => item.title === previousTitle)
        ?.subNav;
      const fifthMenu = fourthMenu?.find((item) => item.title === activeTitle)
        ?.subNav;
      return fifthMenu;
    }
  };

  return (
    <div className={styles.mobileMenuInner}>
      {state.active.title ? (
        <div className={styles.mobileMenuTitleBar}>
          <button aria-label="Back" onClick={() => dispatch({ type: 'back' })}>
            <BoxIcon name="left-arrow-alt" size="xl" />
          </button>
          <p>{state.active.title}</p>
        </div>
      ) : (
        <div className={styles.mobileMenuControls}>
          <ButtonStandard
            data-testid="headerEnquireButton"
            color={COLOR_LIME}
            aria-label="Enquire"
            size="md"
            boxIcon="edit-alt"
            iconPosition="left"
            onClick={() =>
              dispatch({
                type: 'forward',
                payload: {
                  title: 'Enquire'
                }
              })
            }
          >
            Enquire now
          </ButtonStandard>
          {headOfficePhoneNumber && (
            <ButtonLinkStandard
              data-testid="headerPhoneNumberButton"
              color={COLOR_WHITE}
              aria-label="Phone"
              size="md"
              href={`tel:${headOfficePhoneNumber}`}
              className={styles.phoneButton}
              boxIcon="phone"
              iconPosition="left"
            >
              {headOfficePhoneNumber}
            </ButtonLinkStandard>
          )}
        </div>
      )}
      {/* TOP MENU */}
      <Menu
        state={state}
        dispatch={dispatch}
        width={width}
        menuLinks={insertCommunityNav}
        title={undefined}
        index={0}
        handleCloseMobileMenu={handleCloseMobileMenu}
      />

      {/* SUB NAV MENU */}
      <Menu
        state={state}
        dispatch={dispatch}
        menuLinks={
          insertCommunityNav.filter(
            (item) => item.title === state.active.title
          )?.[0]?.subNav
        }
        title={state.active.title}
        index={1}
        handleCloseMobileMenu={handleCloseMobileMenu}
      />

      {/* FIND COMMUNITY CITIES MENU */}
      {state.previous?.length === 2 && (
        <Menu
          state={state}
          dispatch={dispatch}
          menuLinks={getFindYourCommunityMenuByLevel(
            3,
            state,
            findYourCommunityMenu || []
          )}
          title={state.active.title}
          index={2}
          handleCloseMobileMenu={handleCloseMobileMenu}
        />
      )}

      {/* FIND COMMUNITY AREAS MENU */}
      {state.previous?.length === 3 && (
        <Menu
          state={state}
          dispatch={dispatch}
          menuLinks={getFindYourCommunityMenuByLevel(
            4,
            state,
            findYourCommunityMenu || []
          )}
          title={state.active.title}
          index={3}
          handleCloseMobileMenu={handleCloseMobileMenu}
        />
      )}

      {/* FIND COMMUNITY SUBURBS MENU */}
      {state.previous?.length === 4 && (
        <Menu
          state={state}
          dispatch={dispatch}
          menuLinks={getFindYourCommunityMenuByLevel(
            5,
            state,
            findYourCommunityMenu || []
          )}
          title={state.active.title}
          index={4}
          handleCloseMobileMenu={handleCloseMobileMenu}
        />
      )}

      {state.active.title === 'Enquire' && (
        <EnquireMenu
          width={width}
          state={state}
          dispatch={dispatch}
          handleCloseMobileMenu={handleCloseMobileMenu}
          enquireButtonScrollTo={enquireButtonScrollTo}
        />
      )}
    </div>
  );
};

export default MobileNestedMenu;
